import { BottomSheet, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { HCF_PROFILE_SOURCE } from "@src/constants";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";

import { CbhFeatureFlag, useCbhFlag } from "../FeatureFlags";
import { useToast } from "../lib";
import { logError } from "../lib/analytics";
import { useSearchParams } from "../lib/utils/useSearchParams";
import { useFooterData } from "../redesign/Shift/BottomSheet/useFooterData";
import { invalidateShiftModal } from "../redesign/Shift/Open/useGetShiftModal";
import { type ModalShiftData } from "../redesign/Shift/types";
import { SHIFT_DISCOVERY_SHIFT_NEGOTIATION_CANCEL_MODAL_PATH } from "../redesign/ShiftDiscovery/paths";
import { useSupportLinks } from "../support/hooks/useSupportLinks";
import { useZendeskArticle } from "../Zendesk/useZendeskArticle";
import { useCreateNegotiation } from "./api/useCreateNegotiation";
import { useGetNegotiationShiftDetails } from "./api/useGetNegotiationShiftDetails";
import { useProposeNewRate } from "./api/useUpdateNegotiationRate";
import { RateNegotiationDialogContent } from "./RateNegotiationDialogContent";
import { RateNegotiationDialogFooter } from "./RateNegotiationDialogFooter";
import { getLowerAndUpperLimit } from "./utils";

interface RateNegotiationBottomSheetProps {
  shift: ModalShiftData;
  modalState: UseModalState;
}

export function RateNegotiationBottomSheet(props: RateNegotiationBottomSheetProps) {
  const { shift, modalState } = props;

  const { linkSource: linkSourceParameter } = useSearchParams();
  const history = useHistory();
  const queryClient = useQueryClient();

  const { showSuccessToast, showErrorToast } = useToast();

  const flagLimits = useCbhFlag(CbhFeatureFlag.RATE_NEGOTIATION_LIMITS, {
    defaultValue: {
      Maximum: "200",
      Minimum: "15",
    },
  });

  const userHasChangedRate = useRef<boolean>(false);

  const shiftOffer = shift?.offer;
  const offerHourlyPay = shiftOffer?.attributes.hourlyPay ?? 0;
  const lastOfferHourlyPay = shift?.rateNegotiation?.lastOffer?.hourlyPay ?? 0;

  const { data: negotiationShiftDetails } = useGetNegotiationShiftDetails(
    { shiftId: shift.id, offerId: shiftOffer?.id ?? "" },
    { enabled: isDefined(shift.id) }
  );
  const { lowerLimit, upperLimit } = getLowerAndUpperLimit({
    flagLimits,
    shift,
    negotiationShiftDetails,
  });

  const [currentRate, setCurrentRate] = useState<number>(lowerLimit);

  const { mutateAsync: createNegotiation, isLoading: isCreatingNegotiation } =
    useCreateNegotiation();

  const { mutateAsync: proposeNewRate, isLoading: isProposingNewRate } = useProposeNewRate({
    rateNegotiationId: shift.rateNegotiation?.id,
  });

  const increasePayRate = () => {
    userHasChangedRate.current = true;
    setCurrentRate((previousRate) => {
      if (previousRate + 1 > upperLimit) {
        return upperLimit;
      }

      return Math.floor(previousRate + 1);
    });
  };

  const decreasePayRate = () => {
    userHasChangedRate.current = true;
    setCurrentRate((previousRate) => {
      if (lowerLimit > previousRate - 1) {
        return lowerLimit;
      }

      return Math.ceil(previousRate - 1);
    });
  };

  const handleProposeNewRate = async () => {
    try {
      const negotiationId = shift.rateNegotiation?.id;
      if (negotiationId) {
        await proposeNewRate({
          shiftId: shift.id,
          payRate: currentRate,
        });

        showSuccessToast("You have proposed a new rate.");
      } else {
        await createNegotiation({
          shiftId: shift.id,
          offerId: shiftOffer?.id ?? "",
          payRate: currentRate,
          source:
            linkSourceParameter === HCF_PROFILE_SOURCE.PERMANENT_PLACEMENTS_WORK_TRIALS
              ? "PERMANENT_PLACEMENTS_WORK_TRIALS"
              : undefined,
        });

        showSuccessToast("You have started the rate negotiation.");
      }

      await invalidateShiftModal(queryClient, shift.id);
      history.goBack();
    } catch (error) {
      logError("Error Creating/Proposing New Rate Negotiation", {
        error: JSON.stringify((error as Error)?.stack),
      });
      showErrorToast("Error Creating/Proposing New Rate Negotiation");
    }
  };

  const { openArticle } = useZendeskArticle();
  const { rateNegotiationWorkerSupportLink } = useSupportLinks();

  const { isBookable, isInstantBookDisabled, navigateToModal } = useFooterData();

  const openRateNegotiationSupportLink = async () => {
    await openArticle(rateNegotiationWorkerSupportLink);
  };

  const handleEndNegotiation = () => {
    navigateToModal(SHIFT_DISCOVERY_SHIFT_NEGOTIATION_CANCEL_MODAL_PATH, {
      pathParams: { shiftId: shift.id },
    });
  };

  useEffect(() => {
    if (!userHasChangedRate.current) {
      setCurrentRate(lastOfferHourlyPay || offerHourlyPay);
    }
  }, [lastOfferHourlyPay, offerHourlyPay]);

  if (!isDefined(shift)) {
    return null;
  }

  return (
    <BottomSheet modalState={modalState}>
      <RateNegotiationDialogContent
        shift={shift}
        openRateNegotiationSupportLink={openRateNegotiationSupportLink}
      />
      <RateNegotiationDialogFooter
        currentRate={currentRate}
        increaseRate={increasePayRate}
        decreaseRate={decreasePayRate}
        isCreatingNegotiation={isCreatingNegotiation || isProposingNewRate}
        handleProposeNewRate={handleProposeNewRate}
        handleEndNegotiation={handleEndNegotiation}
        shift={shift}
        lowerLimit={lowerLimit}
        upperLimit={upperLimit}
        isBookable={isBookable}
        isInstantBookDisabled={isInstantBookDisabled}
        navigateToModal={navigateToModal}
      />
    </BottomSheet>
  );
}
